import { FunctionRequest, FunctionId, FunctionResponse } from "./protocol.js";
import { JSONValue, convexToJson, jsonToConvex } from "@convex-dev/common";
import { createError, logToConsole } from "../logging.js";

type FunctionStatus = {
  message: FunctionRequest;
  onResult: (result: any) => void;
  onFailure: (reason: any) => void;
};

export class FunctionManager {
  private inflightFunctions: Map<FunctionId, FunctionStatus>;

  constructor() {
    this.inflightFunctions = new Map();
  }

  request(
    udfPath: string,
    args: any[],
    functionId: FunctionId
  ): {
    message: FunctionRequest;
    result: Promise<any>;
  } {
    const message: FunctionRequest = {
      type: "Function",
      functionId,
      udfPath,
      args: <JSONValue[]>convexToJson(args),
    };

    const result = new Promise((resolve, reject) => {
      this.inflightFunctions.set(functionId, {
        message,
        onResult: resolve,
        onFailure: reject,
      });
    });

    return { message, result };
  }

  /**
   * Update the state after receiving a function response.
   */
  onResponse(response: FunctionResponse) {
    const functionInfo = this.inflightFunctions.get(response.functionId);
    if (functionInfo === undefined) {
      // Got a response of a message that we don't know about. That shouldn't
      // really happen unless we get duplicate messages or something.
      return;
    }

    this.inflightFunctions.delete(response.functionId);
    const udfPath = functionInfo.message.udfPath;
    for (const line of response.logLines) {
      logToConsole("info", "function", udfPath, line);
    }
    if (response.success) {
      functionInfo.onResult(jsonToConvex(response.result));
    } else {
      logToConsole("error", "function", udfPath, response.result);
      functionInfo.onFailure(createError("function", udfPath, response.result));
    }
  }

  hasInflightFunctions(): boolean {
    return this.inflightFunctions.size > 0;
  }

  restart() {
    // Unlike mutations, functions are not idempotent. When we reconnect to the
    // backend, we don't know if it is safe to resend in-flight functions, so we
    // cancel them and consider them failed.
    // TODO(presley): If we make the server remember it has started executing a
    // function, we can resend here and remove browser to backend connectivity as
    // a source of transient errors. For example, if a function has never reached
    // the server, then we can safely execute it. If a function was executed
    // successfully but response didn't reach the client. Server can return
    // success on reconnect.
    for (const [functionId, functionInfo] of this.inflightFunctions) {
      this.inflightFunctions.delete(functionId);
      const udfPath = functionInfo.message.udfPath;
      functionInfo.onFailure(
        createError("function", udfPath, "Transient error")
      );
    }
  }
}
